import {
    Box, Button, Grid2,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CustomDivider from '../../shared/utils/CustomDivider'
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent'
import { Field, Form, Formik } from 'formik'
import * as yup from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useAuthStore from '../../store/useAuthStore'
import { TextFormField } from '../../shared/components/formfields/TextFormField'
import { useToaster } from '../../shared/utils/ToasterProvider'
import useApartmentStore from '../../store/useApartmentStore'
import ApartmentListTable from './ApartmentListTable'
import { useAllApartmentss } from './FetchAllApartment'
import { Grid_Gap } from '../../shared/constants'
import AdminToolbar from '../admin/components/AdminToolbar'
import ContactPersonList from './ContactPersonList'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import MapPicker from '../../shared/components/MapPicker'
import { LocationSelectorField } from '../../shared/components/LocationSelectorField'
import api from '../../assets/nodeApi/axiosInstance'

const ApartmentList = () => {

    const { apartment, clearApartment } = useApartmentStore()
    const { token, user } = useAuthStore()
    const { showToaster } = useToaster();
    const queryClient = useQueryClient();
    const { data: allApartments, isLoading } = useAllApartmentss({ contact_search: 1 });
    const [isAdd, setIsAdd] = useState(false);
    const [expandedPanel, setExpandedPanel] = useState(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    useEffect(() => {
        if (isAdd) {
            setExpandedPanel('edit-apartment');
        } else {
            setExpandedPanel(false);
        }
    }, [isAdd]);


    const [searchQuery, setSearchQuery] = useState('');
    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    const originalRow = useMemo(() => {
        const userZoneIds = user?.zones?.map((z) => String(z.id)) || user?.zone_id?.map((z) => String(z)) || [];
        return (
            allApartments
                ?.filter((item) => {
                    if (user?.role === "Admin" || user?.role === "Marketing Manager" || user?.role === "Marketing_Executive") return true;
                    if (item.zone_id === null) return true;

                    return userZoneIds.includes(String(item.zone_id));
                })
                .reverse()
                .map((item, index) => ({
                    ...item,
                    id: item.apartment_crm_id,
                    sr_no: index + 1,
                })) || []
        );
    }, [allApartments, user?.zone_id, user?.zones, user?.role]);

    const filteredRows = useMemo(() => {
        if (!searchQuery.trim()) {
            return originalRow;
        }
        const lowercasedQuery = searchQuery.toLowerCase();
        return originalRow.filter(student =>
            Object.values(student).some(val =>
                val != null && val.toString().toLowerCase().includes(lowercasedQuery)
            )
        );
    }, [originalRow, searchQuery]);


    const validationSchema = yup.object().shape({
        apartment_name: yup.string().required("Apartment Name is required"),
        no_of_flats: yup.number(),
        address: yup.string(),
        city_id: yup.string(),
        state_id: yup.string(),
        country_id: yup.string(),
        zone_id: yup.string(),
        pincode: yup.string(),
        latitude: yup.number(),
        longitude: yup.number()

    });
    const isMarketing = user?.role === "Marketing Executive";

    const initialValues = {
        apartment_name: apartment?.apartment_name || "",
        no_of_flats: apartment?.no_of_flats || "",
        address: apartment?.address || "",
        street: apartment?.street || "",
        landmark: apartment?.landmark || "",
        city_id: isMarketing ? (user?.city_id || apartment?.city_id || "") : (apartment?.city_id || ""),
        state_id: isMarketing ? (user?.state_id || apartment?.state_id || "") : (apartment?.state_id || ""),
        country_id: isMarketing ? (user?.country_id || apartment?.country_id || "") : (apartment?.country_id || ""),
        pincode: apartment?.pincode || "",
        latitude: apartment?.latitude || "",
        longitude: apartment?.longitude || "",
        zone_id: isMarketing ? (user?.zone_id || apartment?.zone_id || "") : (apartment?.zone_id || "")
    };


    const mutation = useMutation({
        mutationFn: (formData) => {
            return apartment?.id
                ? api.put(`collab-updateApartmentDetails/${apartment?.id}`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
                : api.post(`collab-create-apartment-details`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
        },
        onSuccess: (response) => {
            showToaster('Success', response?.data?.message, 'success')
            queryClient.invalidateQueries({ queryKey: ['collab-apartment-details-list'] });
            queryClient.invalidateQueries({ queryKey: ['all-collab-apartment-details'] });

            clearApartment()
        },
        onError: (error) => {
            if (error?.response?.status === 400) {
                showToaster(error.response.data.message, 'Error', 'error');
            } else {
                showToaster('Error Caught While submitting the form', 'Error', 'error');
            }
        },
    });

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        const obj = {
            cluster_id: 1,
            created_by: user?.id ? Number(user.id) : null,
            ...values,
            pincode: values.pincode?.toString() || "",
        };

        mutation.mutate(obj, {
            onSettled: () => {
                setSubmitting(false);
            },
            onSuccess: () => {
                resetForm();
            }
        });
    }

    useEffect(() => {
        setIsAdd(false)
    }, [apartment])

    return (
        <Box>

            {(apartment?.id || isAdd) && <Box
                sx={{ display: "flex", flexDirection: isAdd ? "column-reverse" : "column" }}
            >
                {!isAdd && <ContactPersonList apartment_id={apartment?.id} />}
                <NormalContainerWithHeader>

                    <Accordion
                        expanded={expandedPanel === 'edit-apartment'}
                        onChange={handleAccordionChange('edit-apartment')}
                        sx={{ bgcolor: "transparent", boxShadow: "none" }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Typography variant='h5'>   {apartment?.id ? "Edit Apartment" : "Add Apartment"}  </Typography>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                                enableReinitialize={true}
                            >
                                {({ resetForm }) => (
                                    <Form>
                                        <Grid2 container spacing={5}>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="apartment_name"
                                                    component={TextFormField}
                                                    label="Apartment Name"
                                                    isRequired={true}
                                                />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="no_of_flats"
                                                    component={TextFormField}
                                                    label="Number Of Flats"
                                                    type={"number"}
                                                />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="address"
                                                    component={TextFormField}
                                                    label="Address"
                                                />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="street"
                                                    component={TextFormField}
                                                    label="Street"
                                                    isRequired={true}
                                                />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="landmark"
                                                    component={TextFormField}
                                                    label="Landmark"
                                                    isRequired={true}
                                                />
                                            </Grid2>

                                            <LocationSelectorField
                                                countryField="country_id"
                                                stateField="state_id"
                                                cityField="city_id"
                                                isStoredId={true}
                                                zipField={"pincode"}
                                                zoneField="zone_id"
                                                isProfile={false}
                                                Input_Width={{ xs: 12, sm: 6 }}
                                                isZone={true}
                                                passedCountryId={isMarketing ? user?.country_id : apartment?.country_id}
                                                passedStateId={isMarketing ? user?.state_id : apartment?.state_id}
                                                passedCityId={isMarketing ? user?.city_id : apartment?.city_id}

                                            />
                                            <Grid2 size={12}>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    Select Apartment Location
                                                </Typography>
                                                <MapPicker />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="latitude"
                                                    component={TextFormField}
                                                    label="Latitude"
                                                    type={"number"}
                                                />
                                            </Grid2>
                                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                                <Field
                                                    name="longitude"
                                                    component={TextFormField}
                                                    label="Longitude"
                                                    type={"number"}
                                                />
                                            </Grid2>
                                        </Grid2>
                                        <CustomDivider />
                                        <Grid2 container spacing={Grid_Gap}>
                                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                                <Button fullWidth variant="outlined" onClick={() => { resetForm(); clearApartment() }} > Clear </Button>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                                <Button fullWidth variant="contained" type="submit"> {Object.keys(apartment).length === 0 ? "Submit" : "Update"} </Button>
                                            </Grid2>
                                        </Grid2>
                                    </Form>
                                )}
                            </Formik>
                        </AccordionDetails>
                    </Accordion>
                    <CustomDivider />
                </NormalContainerWithHeader>
                <CustomDivider orientation='horizontal' />
            </Box>
            }

            <NormalContainerWithHeader
                RightContent={
                    <>
                        <AdminToolbar
                            onSearchChange={handleSearchChange}
                        />
                        {!apartment?.id && <Button variant='contained' startIcon={<AddIcon />} style={{ marginLeft: "1rem" }} onClick={() => setIsAdd(true)}>Add Apartment</Button>}

                    </>
                }
            >
                <ApartmentListTable apartmentRows={filteredRows} isLoading={isLoading} />
            </NormalContainerWithHeader>



        </Box>
    )

}

export default ApartmentList